import { useState, useEffect } from 'react';
import {
  List, ListItem, ListItemText, Typography, Accordion, AccordionSummary, AccordionDetails, Chip, Divider,
  Box, Stack, Grid, Button, Avatar, ListItemAvatar, IconButton, CircularProgress,
  TextField, InputAdornment, MenuItem, Select, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PaymentIcon from '@mui/icons-material/Payment';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import ReviewsIcon from '@mui/icons-material/Reviews';
import { fetchClients, saveClient, uploadCheckImage, deleteCheckImage } from '../api/github';
import AddPaymentModal from './AddPaymentModal';
import SearchIcon from '@mui/icons-material/Search';
import LoadingAnimation from './LoadingAnimation';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export default function ClientList({ isMobile, refreshTrigger }) {
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterField, setFilterField] = useState('nickname');
  const [statusFilter, setStatusFilter] = useState('all');
  const employeeId = localStorage.getItem('userId');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrderForCheck, setSelectedOrderForCheck] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [checkToDelete, setCheckToDelete] = useState(null);

  const MAX_FILE_SIZE = 5 * 1024 * 1024;

  useEffect(() => {
    const loadClients = async () => {
      setLoading(true);
      try {
        const data = await fetchClients();
        setClients(data);
      } catch (error) {
        console.error('Ошибка загрузки клиентов:', error);
        alert(error.message || 'Ошибка загрузки данных');
      } finally {
        setLoading(false);
      }
    };
    loadClients();
  }, [employeeId, refreshTrigger]);

  const handleCheckUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const currentOrder = selectedOrderForCheck;
    const currentClientId = selectedClientId;

    if (!currentOrder || !currentClientId) {
      alert('Выберите заказ и клиента');
      return;
    }

    try {
      setIsSubmitting(true);
      const uploadedUrls = await Promise.all(
        files.map(file => {
          if (file.size > MAX_FILE_SIZE) {
            throw new Error(`Файл ${file.name} слишком большой. Максимальный размер: 5MB`);
          }
          return uploadCheckImage(file);
        })
      );

      const updatedClients = [...clients];
      const clientIndex = updatedClients.findIndex(c => c.id === currentClientId);
      const orderIndex = updatedClients[clientIndex].orders.findIndex(o => o.id === currentOrder.id);

      const existingChecks = Array.isArray(updatedClients[clientIndex].orders[orderIndex].checkImages)
        ? updatedClients[clientIndex].orders[orderIndex].checkImages.map(check =>
            typeof check === 'string' ? { id: `check_${Date.now()}_${Math.random()}`, url: check, isPaid: true } : check
          )
        : [];

      const newChecks = uploadedUrls.map(url => ({
        id: `check_${Date.now()}_${Math.random()}`,
        url,
        isPaid: true
      }));

      updatedClients[clientIndex].orders[orderIndex].checkImages = [
        ...existingChecks,
        ...newChecks
      ];

      await saveClient(updatedClients[clientIndex]);
      setClients(updatedClients);
    } catch (error) {
      console.error("Ошибка загрузки чеков:", error);
      alert(error.message || 'Ошибка загрузки чеков');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCheck = async (checkId, order, clientId) => {
    try {
      setIsSubmitting(true);

      const updatedClients = [...clients];
      const clientIndex = updatedClients.findIndex(c => c.id === clientId);
      const orderIndex = updatedClients[clientIndex].orders.findIndex(o => o.id === order.id);

      const check = updatedClients[clientIndex].orders[orderIndex].checkImages.find(c => c.id === checkId);
      if (check) {
        await deleteCheckImage(check.url);

        updatedClients[clientIndex].orders[orderIndex].checkImages =
          updatedClients[clientIndex].orders[orderIndex].checkImages.filter(c => c.id !== checkId);

        await saveClient(updatedClients[clientIndex]);
        setClients(updatedClients);
      }
    } catch (error) {
      console.error("Ошибка при удалении чека:", error);
      alert(error.message || 'Ошибка при удалении чека');
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDeleteCheck = async () => {
    if (!checkToDelete || !selectedOrderForCheck || !selectedClientId) return;

    try {
      setIsSubmitting(true);

      const updatedClients = [...clients];
      const clientIndex = updatedClients.findIndex(c => c.id === selectedClientId);
      const orderIndex = updatedClients[clientIndex].orders.findIndex(o => o.id === selectedOrderForCheck.id);

      const check = updatedClients[clientIndex].orders[orderIndex].checkImages.find(c => c.id === checkToDelete);
      if (check) {
        await deleteCheckImage(check.url);

        updatedClients[clientIndex].orders[orderIndex].checkImages =
          updatedClients[clientIndex].orders[orderIndex].checkImages.filter(c => c.id !== checkToDelete);

        await saveClient(updatedClients[clientIndex]);
        setClients(updatedClients);
      }
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error("Ошибка при удалении чека:", error);
      alert(error.message || 'Ошибка при удалении чека');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleCheckPayment = async (checkId, isCurrentlyPaid, order, clientId) => {
    if (!order || !clientId) {
      alert('Выберите заказ и клиента');
      return;
    }

    try {
      setIsSubmitting(true);

      const updatedClients = [...clients];
      const clientIndex = updatedClients.findIndex(c => c.id === clientId);
      if (clientIndex === -1) {
        throw new Error('Клиент не найден');
      }

      const orderIndex = updatedClients[clientIndex].orders.findIndex(o => o.id === order.id);
      if (orderIndex === -1) {
        throw new Error('Заказ не найден');
      }

      updatedClients[clientIndex].orders[orderIndex].checkImages =
        updatedClients[clientIndex].orders[orderIndex].checkImages.map(check =>
          check.id === checkId
            ? {
                ...check,
                isPaid: true,
                paidDate: !isCurrentlyPaid ? new Date().toISOString() : null
              }
            : check
        );

      await saveClient(updatedClients[clientIndex]);
      setClients(updatedClients);
    } catch (error) {
      console.error("Ошибка при обновлении чека:", error);
      alert(error.message || 'Ошибка при обновлении чека');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Йозилмаган';
    const date = new Date(dateString);
    return date.toLocaleString('uz-UZ', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatMoney = (value) => {
    if (!value) return '';
    const num = value.toString().replace(/\D/g, '');
    return num.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  const formatCurrency = (amount) => {
    return amount ? `${formatMoney(amount.toString())} Сум` : '0 Сум';
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch =
      filterField === 'nickname' && client.nickname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      filterField === 'code' && client.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      filterField === 'employee' && client.orders?.some(o => o.employeeName?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      filterField === 'date' && client.orders?.some(order =>
        formatDateTime(order.date).toLowerCase().includes(searchTerm.toLowerCase())
      );

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'paid' && client.orders?.some(o => o.status === 'Тўллиқ тўланган')) ||
      (statusFilter === 'unpaid' && client.orders?.some(o => o.status !== 'Тўллиқ тўланган'));

    return matchesSearch && matchesStatus;
  });

  const handleAddPayment = async (clientId, orderId, paymentData) => {
    try {
      const clients = await fetchClients();
      const clientIndex = clients.findIndex(c => c.id === clientId);

      if (clientIndex === -1) throw new Error('Клиент не найден');

      const orderIndex = clients[clientIndex].orders.findIndex(o => o.id === orderId);
      if (orderIndex === -1) throw new Error('Заказ не найден');

      const order = clients[clientIndex].orders[orderIndex];

      if (!order.paymentInfo.prepayments) {
        order.paymentInfo.prepayments = [];
      }

      order.paymentInfo.prepayments.push({
        amount: paymentData.amount,
        date: paymentData.date,
        comment: paymentData.comment || ''
      });

      order.paymentInfo.paidAmount = order.paymentInfo.prepayments.reduce(
        (sum, payment) => sum + parseFloat(payment.amount || 0),
        0
      );

      if (order.paymentInfo.paidAmount >= order.totalAmount) {
        order.status = 'Тўллиқ тўланган';
      } else if (order.paymentInfo.paidAmount > 0) {
        order.status = 'Охиргача тўланмаган';
      }

      await saveClient(clients[clientIndex]);
      setClients(clients);
      return true;
    } catch (error) {
      console.error("Ошибка при сохранении платежа:", error);
      throw error;
    }
  };

  return (
    <div>
      {loading ? (
        <LoadingAnimation loading={loading} />
      ) : clients.length === 0 ? (
        <Typography>Мижозлар хақида малумот йўқ</Typography>
      ) : (
        <List>
          <Box sx={{ mb: 2, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
            <FormControl sx={{ minWidth: 120 }} size="small">
              <InputLabel>Фильтр по</InputLabel>
              <Select
                value={filterField}
                onChange={(e) => setFilterField(e.target.value)}
                label="Фильтр по"
              >
                <MenuItem value="employee">Ходим</MenuItem>
                <MenuItem value="nickname">Ник</MenuItem>
                <MenuItem value="code">Код</MenuItem>
                <MenuItem value="date">Сана</MenuItem>
              </Select>
            </FormControl>
            <TextField
              placeholder="Қидириш..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              fullWidth
              size="small"
            />
            <FormControl sx={{ minWidth: 120 }} size="small">
              <InputLabel>Тўлов ҳолати</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Тўлов ҳолати"
              >
                <MenuItem value="all">Ҳаммаси</MenuItem>
                <MenuItem value="paid">Тўланган</MenuItem>
                <MenuItem value="unpaid">Тўланмаган</MenuItem>
              </Select>
            </FormControl>
          </Box>
          {filteredClients.map(client => (
            <Accordion key={client.id}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <ListItemText
                  primary={"Мижоз " + client.nickname}
                  sx={{ mr: 1 }}
                />
                <ListItemText
                  primary={" Ходим  " + client.orders[0]?.employeeName}
                />
                <Chip
                  label={formatCurrency(client.orders[0]?.paymentInfo?.paidAmount || 0)}
                  sx={{
                    ml: 2,
                    color: 'white',
                    background: client.orders[0]?.status === 'Тўллиқ тўланган' ? 'green' : 'red'
                  }}
                />
              </AccordionSummary>
              <AccordionDetails>
                {client.orders?.map(order => (
                  <Box key={order.id} sx={{ mb: 3 }}>
                    {order.employeeName && (
                      <Typography variant="subtitle1" gutterBottom>
                        {order.employeeName} дан буюртма {formatDateTime(order.date)}
                      </Typography>
                    )}
                    <Typography variant="h7" gutterBottom>
                      Код: {client.code}
                    </Typography>
                    <List dense>
                      {order.products.map((item, idx) => (
                        <ListItem key={idx}>
                          <ListItemAvatar>
                            <Avatar
                              src={item.productImage}
                              alt={item.productName}
                              variant="square"
                              sx={{ width: 56, height: 56, mr: 2 }}
                            />
                          </ListItemAvatar>
                          <ListItemText
                            primary={`${item.productName || 'Махсулот'} x${item.quantity}`}
                            secondary={
                              <>
                                <Typography component="span" display="block">
                                  {formatCurrency(item.productPrice * item.quantity)}
                                </Typography>
                                {item.productDescription && (
                                  <Typography component="span" variant="body2" color="text.secondary">
                                    {item.productDescription}
                                  </Typography>
                                )}
                              </>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                    <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
                      Жами: {formatCurrency(order.totalAmount)}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="center" gap={1} mb={1}>
                        <PaymentIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle2">Тўлов маълумотлари</Typography>
                      </Stack>
                      <TableContainer component={Paper} sx={{ mb: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Сумма</TableCell>
                              <TableCell>Сана</TableCell>
                              <TableCell>Изоҳ</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {order.paymentInfo.prepayments?.map((payment, index) => (
                              <TableRow key={index}>
                                <TableCell>{formatCurrency(payment.amount)}</TableCell>
                                <TableCell>{formatDateTime(payment.date)}</TableCell>
                                <TableCell>{payment.comment || 'Йозилмаган'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Grid container spacing={2}>
                        {order.status !== 'Тўллиқ тўланган' && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PaymentIcon />}
                            onClick={() => {
                              setSelectedOrder(order);
                              setSelectedClientId(client.id);
                              setPaymentModalOpen(true);
                            }}
                            fullWidth
                            sx={{ mt: 1 }}
                          >
                            Тўлов қўшиш
                          </Button>
                        )}
                        <Grid>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#460101' }}>
                            Махсулот: {formatCurrency(order.totalAmount)}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            Жами тўланган: {formatCurrency(order.paymentInfo.paidAmount)}
                          </Typography>
                          {order.paymentInfo.paidAmount > order.totalAmount && (
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'green' }}>
                              Ортиқча тўлов: {formatCurrency(order.paymentInfo.paidAmount - order.totalAmount)}
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'red' }}>
                            {order.totalAmount - order.paymentInfo.paidAmount > 0 ? "Қолган тўлов: " + formatCurrency(Math.max(0, order.totalAmount - order.paymentInfo.paidAmount)) : ""}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="center" gap={1} mb={1}>
                        <ImageIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle2">Чеклар</Typography>
                      </Stack>
                      {order.checkImages?.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                          {order.checkImages.map((check) => {
                            const checkId = check.id;
                            const checkUrl = check.url;
                            const isPaid = check.isPaid;
                            const paidDate = check.paidDate;

                            return (
                              <Box
                                key={checkId}
                                sx={{
                                  position: 'relative',
                                  border: isPaid ? '2px solid green' : '2px solid transparent',
                                  borderRadius: 1
                                }}
                              >
                                <img
                                  src={checkUrl}
                                  alt={`Чек ${checkId}`}
                                  style={{
                                    width: 150,
                                    height: 150,
                                    objectFit: 'cover',
                                    borderRadius: 1,
                                  }}
                                  onClick={() => {
                                    setSelectedOrderForCheck(order);
                                    setSelectedClientId(client.id);
                                    setCheckToDelete(checkId);
                                  }}
                                />
                                <Box sx={{
                                  position: 'absolute',
                                  top: 0,
                                  right: 0,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 0.5
                                }}>
                                  <IconButton
                                    size="small"
                                    sx={{
                                      backgroundColor: 'rgba(255,255,255,0.7)',
                                      color: isPaid ? 'success.main' : 'action.disabled'
                                    }}
                                    onClick={() => {
                                      setSelectedOrderForCheck(order);
                                      setSelectedClientId(client.id);
                                      handleToggleCheckPayment(checkId, isPaid, order, client.id);
                                    }}
                                    disabled={isSubmitting}
                                  >
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    sx={{
                                      backgroundColor: 'rgba(255,255,255,0.7)'
                                    }}
                                    onClick={() => window.open(checkUrl, '_blank')}
                                  >
                                    <ZoomInIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    sx={{
                                      backgroundColor: 'rgba(255,255,255,0.7)',
                                      color: 'error.main'
                                    }}
                                    onClick={() => {
                                      setCheckToDelete(checkId);
                                      setSelectedOrderForCheck(order);
                                      setSelectedClientId(client.id);
                                      setDeleteDialogOpen(true);
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                                {isPaid && paidDate && (
                                  <Typography variant="caption" sx={{
                                    position: 'absolute',
                                    bottom: 4,
                                    left: 4,
                                    backgroundColor: 'rgba(0,0,0,0.7)',
                                    color: 'white',
                                    px: 0.5,
                                    borderRadius: 1
                                  }}>
                                    {formatDateTime(paidDate)}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          Чеклар йўқ
                        </Typography>
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={isSubmitting ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                        disabled={isSubmitting}
                        onClick={() => {
                          setSelectedOrderForCheck(order);
                          setSelectedClientId(client.id);
                          document.getElementById(`check-upload-input-${order.id}`).click();
                        }}
                        fullWidth
                      >
                        Чек қўшиш
                        <input
                          id={`check-upload-input-${order.id}`}
                          type="file"
                          hidden
                          accept="image/*"
                          multiple
                          onChange={handleCheckUpload}
                        />
                      </Button>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="center" gap={1} mb={1}>
                        <DeliveryDiningIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle2">Етказиб бериш</Typography>
                      </Stack>
                      <Typography variant="body2" gutterBottom>
                        Манзил: {order.deliveryInfo?.address || 'Йозилмаган'}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid>
                          <Typography variant="body2">
                            Почта чиқарилиш вақт: {formatDateTime(order.deliveryInfo?.scheduledTime)}
                          </Typography>
                        </Grid>
                        <Grid>
                          <Typography variant="body2">
                            Почта Йетиб бориш вақт: {formatDateTime(order.deliveryInfo?.actualTime)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="center" gap={1} mb={1}>
                        <Typography variant="subtitle2">Той маълумотлари</Typography>
                      </Stack>
                      <Typography variant="body2" gutterBottom>
                        Той санаси: {formatDateTime(order.partyDate)}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        Той маълумотлари: {order.partyComment ? order.partyComment : 'Малумот йўқ'}
                      </Typography>
                    </Box>
                    {order.review && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Box>
                          <Stack direction="row" alignItems="center" gap={1} mb={1}>
                            <ReviewsIcon color="primary" fontSize="small" />
                            <Typography variant="subtitle2">Мижоз баҳоси</Typography>
                          </Stack>
                          <Typography variant="body2">
                            {order.review}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </List>
      )}
      <ConfirmDeleteModal
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDeleteCheck}
        itemName="Чек"
        title="Чекни ўчириш"
        message="Хақиқатан хам бу чекни ўчирмоқчимисиз?"
        confirmText="Ҳа"
        cancelText="Йўқ"
      />
      <AddPaymentModal
        open={paymentModalOpen}
        onClose={(shouldRefresh) => {
          setPaymentModalOpen(false);
          if (shouldRefresh) {
            // Обновление данных будет происходить через refreshTrigger
          }
        }}
        order={selectedOrder || {
          totalAmount: 0,
          paymentInfo: {
            prepayments: [],
            paidAmount: 0
          }
        }}
        clientId={selectedClientId}
        onAddPayment={handleAddPayment}
      />
    </div>
  );
}